import { useEffect, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import ExportButton from "@/components/ExportButton";
import PdfButton from "@/components/PdfButton";
import BarcodeScanner from "@/components/BarcodeScanner";
import { parseGs1, looksLikeGs1 } from "@/lib/gs1";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PackageOpen, ScanLine } from "lucide-react";
import HelpButton from "@/components/HelpButton";

export default function Receival() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const canEdit = user?.role !== "auditor";

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    supplier_id: "",
    product: "",
    quantity: "",
    delivery_temp_c: "",
    use_by_date: "",
    batch_number: "",
    condition: "good",
    accepted: "true",
    rejection_reason: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleScan = (code) => {
    setScannerOpen(false);
    if (!code) return;
    // GS1 DataMatrix / QR on supplier boxes encodes structured Application
    // Identifiers — pull batch + use-by + quantity straight into the form
    // so receiving staff don't have to re-key data the supplier already
    // printed on the carton.
    if (looksLikeGs1(code)) {
      const gs1 = parseGs1(code);
      if (gs1) {
        setForm((f) => ({
          ...f,
          batch_number: gs1.batch || f.batch_number,
          use_by_date: gs1.use_by ? `${gs1.use_by}T00:00` : f.use_by_date,
          quantity: gs1.quantity ? `${gs1.quantity}${f.quantity ? ` (${f.quantity})` : ""}` : f.quantity,
        }));
        const parts = [];
        if (gs1.batch) parts.push(`batch ${gs1.batch}`);
        if (gs1.use_by) parts.push(`use-by ${gs1.use_by}`);
        if (gs1.gtin) parts.push(`GTIN ${gs1.gtin}`);
        toast.success(
          parts.length
            ? `GS1 scanned: ${parts.join(" · ")}`
            : "GS1 code recognised — fields updated"
        );
        return;
      }
    }
    // Plain barcode (Code128 batch number on a label, EAN on a product) —
    // treat the whole string as the batch number.
    setForm((f) => ({ ...f, batch_number: code }));
    toast.success(`Batch number captured: ${code}`);
  };

  const load = async () => {
    if (!activeSite) return;
    const [sup, lg] = await Promise.all([
      api.get("/suppliers", withSite(activeSite.id)),
      api.get("/receival", withSite(activeSite.id)),
    ]);
    setSuppliers(sup.data);
    setItems(lg.data);
    if (sup.data.length && !form.supplier_id)
      setForm((f) => ({ ...f, supplier_id: sup.data[0].id }));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [activeSite]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id || !form.product) return;
    setBusy(true);
    try {
      await api.post(
        "/receival",
        {
          ...form,
          delivery_temp_c: form.delivery_temp_c ? parseFloat(form.delivery_temp_c) : null,
          accepted: form.accepted === "true",
        },
        withSite(activeSite.id)
      );
      toast.success("Receival recorded");
      setForm({ ...form, product: "", quantity: "", delivery_temp_c: "", batch_number: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="receival-page">
      <PageHeader
        title="Food Receival & Supplier Verification"
        subtitle="Record every delivery — supplier, product, temperature on arrival, condition and batch. Rejected goods are tracked for supplier review."
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="receival" />
            <PdfButton kind="receival" />
            <HelpButton topic="receival" />
          </div>
        }
      />

      {canEdit && (
        <form onSubmit={submit} className="surface p-6 md:p-8 mb-8 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-3 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <PackageOpen className="w-5 h-5 text-eucalyptus-700" />
              <h2 className="font-display font-bold text-lg">New delivery</h2>
            </div>
            <Button
              type="button"
              data-testid="rec-scan"
              onClick={() => setScannerOpen(true)}
              className="h-12 bg-ochre-600 hover:bg-ochre-400 text-white gap-2"
            >
              <ScanLine className="w-4 h-4" /> Scan batch barcode
            </Button>
          </div>

          <div className="space-y-2">
            <Label>Supplier</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
              <SelectTrigger data-testid="rec-supplier" className="h-12">
                <SelectValue placeholder="Supplier" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Product</Label>
            <Input
              data-testid="rec-product"
              value={form.product}
              onChange={(e) => setForm({ ...form, product: e.target.value })}
              className="h-12"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Quantity</Label>
            <Input
              data-testid="rec-quantity"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label>Delivery temp (°C)</Label>
            <Input
              data-testid="rec-temp"
              type="number"
              step="0.1"
              value={form.delivery_temp_c}
              onChange={(e) => setForm({ ...form, delivery_temp_c: e.target.value })}
              className="h-12 font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label>Use-by date</Label>
            <Input
              data-testid="rec-useby"
              type="date"
              value={form.use_by_date}
              onChange={(e) => setForm({ ...form, use_by_date: e.target.value })}
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label>Batch #</Label>
            <Input
              data-testid="rec-batch"
              value={form.batch_number}
              onChange={(e) => setForm({ ...form, batch_number: e.target.value })}
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label>Condition</Label>
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger data-testid="rec-condition" className="h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="spoiled">Spoiled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Accepted</Label>
            <Select value={form.accepted} onValueChange={(v) => setForm({ ...form, accepted: v })}>
              <SelectTrigger data-testid="rec-accepted" className="h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Accepted</SelectItem>
                <SelectItem value="false">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Notes / rejection reason</Label>
            <Input
              data-testid="rec-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="h-12"
            />
          </div>

          <div className="md:col-span-3 flex justify-end">
            <Button
              type="submit"
              disabled={busy}
              data-testid="rec-submit"
              className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
            >
              Record delivery
            </Button>
          </div>
        </form>
      )}

      <div className="surface overflow-hidden">
        <div className="grid grid-cols-12 bg-bone-alt px-6 py-3 text-[11px] uppercase font-bold tracking-widest text-eucalyptus-700">
          <div className="col-span-3">Product</div>
          <div className="col-span-2">Supplier</div>
          <div className="col-span-1 text-center">Temp</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-2">Batch / Use-by</div>
          <div className="col-span-3">When · By</div>
        </div>
        {items.length === 0 ? (
          <div className="p-10 text-center text-charcoal-soft text-sm">No deliveries recorded.</div>
        ) : (
          items.map((l) => (
            <div
              key={l.id}
              className="grid grid-cols-12 px-6 py-4 border-t border-[color:var(--border)] items-center"
            >
              <div className="col-span-3 font-medium">{l.product}</div>
              <div className="col-span-2 text-sm">{l.supplier_name}</div>
              <div className="col-span-1 text-center font-mono">
                {l.delivery_temp_c != null ? `${l.delivery_temp_c}°` : "—"}
              </div>
              <div className="col-span-1 text-center">
                {l.accepted ? (
                  <Badge className="bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-100">
                    Accepted
                  </Badge>
                ) : (
                  <Badge className="bg-ochre-600 text-white hover:bg-ochre-600">Rejected</Badge>
                )}
              </div>
              <div className="col-span-2 text-xs font-mono">
                {l.batch_number || "—"} · {l.use_by_date || "—"}
              </div>
              <div className="col-span-3 text-xs text-charcoal-soft font-mono">
                {new Date(l.created_at).toLocaleString("en-AU")} · {l.user_name}
              </div>
            </div>
          ))
        )}
      </div>

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={handleScan}
        title="Scan delivery batch barcode"
      />
    </div>
  );
}
